import { cn } from "lib/utils";
import { ArrowRight, CheckCircle2, Globe, Lock, Shield } from "lucide-react";
import type React from "react";
import { lpConfig } from "routes/LandingPage/lpConfig";
import { useFadeIn } from "routes/LandingPage/useFadeIn";

const ICON_MAP: Record<string, React.ElementType> = {
  Shield,
  Lock,
  Globe,
  CheckCircle2,
};

/**
 * ランディングページのヒーローセクション
 * 中央配置のキャッチコピー + 下部にモックダッシュボードUIを表示する
 * @return {JSX.Element} ヒーローセクションコンポーネント
 */
export function LpHero() {
  const text = useFadeIn();
  const ui = useFadeIn(0.1);

  return (
    <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#02072F] overflow-hidden">
      {/* Base gradient layer */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#020830] via-[#0c1654] to-[#150d3a]" />

      {/* Gradient orbs - high visibility */}
      <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full opacity-30 blur-[80px] bg-[#1e40af]" />
      <div className="absolute top-1/4 -right-40 w-[500px] h-[500px] rounded-full opacity-25 blur-[80px] bg-[#4f46e5]" />
      <div className="absolute -bottom-40 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[80px] bg-[#7c3aed]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] rounded-full opacity-15 blur-[60px] bg-[#2563eb]" />

      {/* Network graph SVG overlay */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.12]"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Edges */}
        <line
          x1="10%"
          y1="15%"
          x2="25%"
          y2="35%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="25%"
          y1="35%"
          x2="45%"
          y2="20%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="45%"
          y1="20%"
          x2="65%"
          y2="30%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="65%"
          y1="30%"
          x2="85%"
          y2="15%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="85%"
          y1="15%"
          x2="92%"
          y2="40%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="25%"
          y1="35%"
          x2="35%"
          y2="60%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="35%"
          y1="60%"
          x2="55%"
          y2="50%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="55%"
          y1="50%"
          x2="65%"
          y2="30%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="55%"
          y1="50%"
          x2="75%"
          y2="65%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="75%"
          y1="65%"
          x2="92%"
          y2="40%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="5%"
          y1="55%"
          x2="20%"
          y2="70%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="20%"
          y1="70%"
          x2="35%"
          y2="60%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="20%"
          y1="70%"
          x2="40%"
          y2="85%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="40%"
          y1="85%"
          x2="60%"
          y2="80%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="60%"
          y1="80%"
          x2="75%"
          y2="65%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="75%"
          y1="65%"
          x2="90%"
          y2="80%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="45%"
          y1="20%"
          x2="55%"
          y2="50%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="10%"
          y1="15%"
          x2="5%"
          y2="55%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="85%"
          y1="15%"
          x2="90%"
          y2="80%"
          stroke="white"
          strokeWidth="0.5"
        />
        <line
          x1="35%"
          y1="60%"
          x2="60%"
          y2="80%"
          stroke="white"
          strokeWidth="0.5"
        />

        {/* Firefly-glow SVG filters */}
        <defs>
          <filter id="glow-soft" x="-300%" y="-300%" width="700%" height="700%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
            </feMerge>
          </filter>
          <filter id="glow-core" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
        </defs>

        {/* Firefly accent filters - tight, intense */}
        <defs>
          <filter id="glow-mid" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <filter id="glow-hot" x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="1.5" />
          </filter>
        </defs>

        {/* Nodes - firefly glow, yellow-green */}
        {/* Node 1 */}
        <circle
          cx="10%"
          cy="15%"
          r="10"
          fill="#a3e635"
          opacity="0.3"
          filter="url(#glow-soft)"
        />
        <circle
          cx="10%"
          cy="15%"
          r="2"
          fill="#fefce8"
          opacity="0.9"
          filter="url(#glow-core)"
        />
        {/* Node 2 ★ FIREFLY */}
        <circle
          cx="25%"
          cy="35%"
          r="12"
          fill="#66ff00"
          opacity="0.7"
          filter="url(#glow-soft)"
        />
        <circle
          cx="25%"
          cy="35%"
          r="6"
          fill="#aaff00"
          opacity="0.9"
          filter="url(#glow-mid)"
        />
        <circle
          cx="25%"
          cy="35%"
          r="3"
          fill="#ccff00"
          opacity="1"
          filter="url(#glow-hot)"
        />
        <circle cx="25%" cy="35%" r="1.5" fill="#eeffaa" opacity="1" />
        {/* Node 3 */}
        <circle
          cx="45%"
          cy="20%"
          r="9"
          fill="#a3e635"
          opacity="0.25"
          filter="url(#glow-soft)"
        />
        <circle
          cx="45%"
          cy="20%"
          r="1.8"
          fill="#fefce8"
          opacity="0.85"
          filter="url(#glow-core)"
        />
        {/* Node 4 (hub) */}
        <circle
          cx="65%"
          cy="30%"
          r="12"
          fill="#a3e635"
          opacity="0.35"
          filter="url(#glow-soft)"
        />
        <circle
          cx="65%"
          cy="30%"
          r="2.5"
          fill="#fefce8"
          opacity="0.95"
          filter="url(#glow-core)"
        />
        {/* Node 5 */}
        <circle
          cx="85%"
          cy="15%"
          r="10"
          fill="#bef264"
          opacity="0.3"
          filter="url(#glow-soft)"
        />
        <circle
          cx="85%"
          cy="15%"
          r="2"
          fill="#fefce8"
          opacity="0.9"
          filter="url(#glow-core)"
        />
        {/* Node 6 */}
        <circle
          cx="92%"
          cy="40%"
          r="9"
          fill="#a3e635"
          opacity="0.25"
          filter="url(#glow-soft)"
        />
        <circle
          cx="92%"
          cy="40%"
          r="1.8"
          fill="#fefce8"
          opacity="0.85"
          filter="url(#glow-core)"
        />
        {/* Node 7 */}
        <circle
          cx="5%"
          cy="55%"
          r="8"
          fill="#bef264"
          opacity="0.2"
          filter="url(#glow-soft)"
        />
        <circle
          cx="5%"
          cy="55%"
          r="1.5"
          fill="#fefce8"
          opacity="0.8"
          filter="url(#glow-core)"
        />
        {/* Node 8 */}
        <circle
          cx="20%"
          cy="70%"
          r="11"
          fill="#a3e635"
          opacity="0.3"
          filter="url(#glow-soft)"
        />
        <circle
          cx="20%"
          cy="70%"
          r="2.2"
          fill="#fefce8"
          opacity="0.9"
          filter="url(#glow-core)"
        />
        {/* Node 9 ★ FIREFLY */}
        <circle
          cx="35%"
          cy="60%"
          r="14"
          fill="#66ff00"
          opacity="0.65"
          filter="url(#glow-soft)"
        />
        <circle
          cx="35%"
          cy="60%"
          r="7"
          fill="#aaff00"
          opacity="0.9"
          filter="url(#glow-mid)"
        />
        <circle
          cx="35%"
          cy="60%"
          r="3.5"
          fill="#ccff00"
          opacity="1"
          filter="url(#glow-hot)"
        />
        <circle cx="35%" cy="60%" r="1.8" fill="#eeffaa" opacity="1" />
        {/* Node 10 */}
        <circle
          cx="55%"
          cy="50%"
          r="12"
          fill="#bef264"
          opacity="0.3"
          filter="url(#glow-soft)"
        />
        <circle
          cx="55%"
          cy="50%"
          r="2.5"
          fill="#fefce8"
          opacity="0.95"
          filter="url(#glow-core)"
        />
        {/* Node 11 ★ FIREFLY */}
        <circle
          cx="75%"
          cy="65%"
          r="11"
          fill="#66ff00"
          opacity="0.6"
          filter="url(#glow-soft)"
        />
        <circle
          cx="75%"
          cy="65%"
          r="5.5"
          fill="#aaff00"
          opacity="0.85"
          filter="url(#glow-mid)"
        />
        <circle
          cx="75%"
          cy="65%"
          r="2.8"
          fill="#ccff00"
          opacity="1"
          filter="url(#glow-hot)"
        />
        <circle cx="75%" cy="65%" r="1.2" fill="#eeffaa" opacity="1" />
        {/* Node 12 */}
        <circle
          cx="40%"
          cy="85%"
          r="9"
          fill="#a3e635"
          opacity="0.25"
          filter="url(#glow-soft)"
        />
        <circle
          cx="40%"
          cy="85%"
          r="1.8"
          fill="#fefce8"
          opacity="0.85"
          filter="url(#glow-core)"
        />
        {/* Node 13 */}
        <circle
          cx="60%"
          cy="80%"
          r="10"
          fill="#a3e635"
          opacity="0.3"
          filter="url(#glow-soft)"
        />
        <circle
          cx="60%"
          cy="80%"
          r="2"
          fill="#fefce8"
          opacity="0.9"
          filter="url(#glow-core)"
        />
        {/* Node 14 */}
        <circle
          cx="90%"
          cy="80%"
          r="8"
          fill="#bef264"
          opacity="0.2"
          filter="url(#glow-soft)"
        />
        <circle
          cx="90%"
          cy="80%"
          r="1.5"
          fill="#fefce8"
          opacity="0.8"
          filter="url(#glow-core)"
        />
      </svg>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Text block - centered */}
        <div
          ref={text.ref}
          className={cn(
            "text-center mb-16 lp-fade-in-up",
            text.isVisible && "lp-visible",
          )}
        >
          <span className="inline-flex items-center bg-white/10 text-[#2B92FF] text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-8">
            {lpConfig.hero.badge}
          </span>

          <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            {lpConfig.hero.headline}
          </h1>

          <p className="text-gray-300 text-lg sm:text-xl font-medium mb-4">
            {lpConfig.hero.subheadline}
          </p>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            {lpConfig.hero.description[0]}
            <br className="hidden sm:inline" />
            {lpConfig.hero.description[1]}
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
            <a
              href={lpConfig.hero.ctaPrimary.href}
              className="inline-flex items-center justify-center gap-2 bg-[#ff8d00] text-white px-8 py-4 rounded-2xl font-bold text-lg shadow-lg hover:opacity-90 transition-opacity"
            >
              {lpConfig.hero.ctaPrimary.label}
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href={lpConfig.hero.ctaSecondary.href}
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 transition-colors"
            >
              {lpConfig.hero.ctaSecondary.label}
            </a>
          </div>

          <div className="flex flex-wrap justify-center gap-6 opacity-60 text-gray-400 text-sm">
            {lpConfig.hero.trustBadges.map(badge => {
              const Icon = ICON_MAP[badge.icon];
              return (
                <span key={badge.label} className="flex items-center gap-2">
                  {Icon && <Icon className="w-4 h-4" />}
                  {badge.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Mock Dashboard UI - below text */}
        <div
          ref={ui.ref}
          className={cn(
            "max-w-3xl mx-auto lp-fade-in-up lp-delay-200",
            ui.isVisible && "lp-visible",
          )}
        >
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm shadow-2xl overflow-hidden">
            {/* Browser header bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <div className="flex-1 mx-4">
                <div className="bg-white/10 rounded-md h-5 max-w-xs" />
              </div>
            </div>

            {/* Mock content */}
            <div className="p-5 sm:p-6 space-y-3">
              {/* Header bar */}
              <div className="flex items-center justify-between mb-4">
                <div className="space-y-2">
                  <div className="bg-white/20 rounded h-4 w-32" />
                  <div className="bg-white/10 rounded h-3 w-48" />
                </div>
                <div className="bg-[#ff8d00]/20 text-[#ff8d00] text-xs font-bold px-3 py-1 rounded-full">
                  {lpConfig.hero.mockMatchBadge}
                </div>
              </div>

              {/* Match result rows */}
              {lpConfig.hero.mockMatches.map(item => (
                <div
                  key={item.name}
                  className="bg-white/10 rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white text-sm font-bold">
                      {item.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">
                        {item.name}
                      </div>
                      <div className="text-gray-400 text-xs">{item.skill}</div>
                    </div>
                  </div>
                  <div className="text-white font-bold text-lg">
                    {item.score}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
